/**
 * Builder logic — real queries/mutations over the workflows endpoints.
 * The editor owns ONE draft definition (pure edits from definitionEdit.ts);
 * Save persists it and Test dry-runs the DRAFT (the server accepts unsaved
 * definitions). Enable is not gated here — the server decides whether a
 * definition is runnable and says what's missing.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMe } from '../../hooks/useMe.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { isScheduleTrigger } from '../../lib/types.js';
import type {
  BuilderDefinition,
  BuilderSchedule,
  BuilderStep,
  BuilderWorkflow,
  WorkflowsOverviewResponse,
} from '../../lib/types.js';
import * as api from './api.js';
import { templateById } from './templates.js';
import type { BuilderStep as Step } from '../../lib/types.js';
import type { StepEditApi } from './stepTypes.js';
import {
  addCase,
  defaultStep,
  insertStep,
  obviousPersonField,
  knownStepTypes,
  listLengthAt,
  moveStep,
  removeCase,
  removeStep,
  replaceStep,
  updateCase,
  type StepPath,
} from './definitionEdit.js';

const asDefinition = (raw: unknown): BuilderDefinition | null => {
  const d = raw as BuilderDefinition | null;
  return d && d.v === 1 && Array.isArray(d.steps) ? d : null;
};

/** Flatten the overview into one list: system entries + composed rows. */
export const flattenOverview = (o: WorkflowsOverviewResponse): BuilderWorkflow[] => [
  ...o.workflows
    .filter((w) => w.route !== null)
    .map((w) => ({
      id: w.route?.id ?? w.key,
      name: w.name,
      kind: 'system' as const,
      trigger: w.trigger,
      enabled: w.route?.isEnabled ?? false,
      version: 1,
      definition: null,
      eventsLast7d: w.route?.eventsLast7d ?? 0,
      systemKey: w.key,
      systemSteps: w.steps,
      systemBranches: w.branches,
      systemConfig: w.route?.config,
      hasConfig: w.hasConfig,
    })),
  ...o.composed.map((c) => ({
    id: c.id,
    name: c.name,
    kind: 'composed' as const,
    trigger: c.trigger,
    schedule: c.schedule,
    enabled: c.enabled,
    version: c.version,
    definition: asDefinition(c.definition),
    lastRun: c.lastRun,
    issues: c.issues,
    eventsLast7d: c.eventsLast7d,
    owner: c.owner,
    endsAt: c.endsAt,
    brokenReason: c.brokenReason,
  })),
];

export const useBuilderListLogic = () => {
  const { data: me } = useMe();
  const tenantId = me?.tenant.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.workflows(tenantId ?? ''),
    queryFn: () => api.fetchOverview(),
    enabled: Boolean(tenantId),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.setWorkflowEnabled(id, enabled),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') }),
  });

  // The server deletes disabled rows only — an enabled workflow is turned
  // off first, in the same action (the confirm modal says so). Runs already
  // recorded keep their snapshots.
  const remove = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      if (enabled) await api.setWorkflowEnabled(id, false);
      await api.deleteComposed(id);
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') }),
  });

  const workflows = useMemo(() => (query.data ? flattenOverview(query.data) : []), [query.data]);

  return {
    workflows,
    unmatched: { count: query.data?.unmatchedLast7d ?? 0 },
    activity: {
      waiting: query.data?.runsWaiting ?? 0,
      failed: query.data?.runsFailed ?? 0,
    },
    approverCandidates: query.data?.approverCandidates ?? [],
    // The list's summary sentence names triggers and tools by their catalog
    // words; destinations live behind another query, so it says how many.
    triggers: query.data?.triggers ?? [],
    toggleEnabled: (id: string) => {
      const w = workflows.find((x) => x.id === id);
      if (w) toggle.mutate({ id, enabled: !w.enabled });
    },
    deleteWorkflow: async (id: string) => {
      const w = workflows.find((x) => x.id === id);
      if (w) await remove.mutateAsync({ id, enabled: w.enabled });
    },
    deleting: remove.isPending,
    deleteError: remove.error,
    isLoading: query.isLoading,
    error: query.error,
  };
};

const NEW_DEFINITION: BuilderDefinition = { v: 1, createCase: false, steps: [] };

/** Per-step delay when the helper's workflow is laid down, and the ceiling on
 * the whole reveal — a ten-step workflow must not take ten steps' worth. */
const REVEAL_STEP_MS = 280;
const REVEAL_TOTAL_MS = 1400;

/**
 * A helper proposal names DM channels that don't exist yet by a placeholder
 * ref. Rewriting those to the editor's own `user:<id>` markers means the SAME
 * save-time path that materialises a hand-picked person creates them — the
 * chat needs no write path of its own.
 */
const resolveChannelRefs = (
  definition: BuilderDefinition,
  pendingChannels?: { ref: string; targetUserId: string }[],
): BuilderDefinition => {
  if (!pendingChannels?.length) return definition;
  const byRef = new Map(pendingChannels.map((c) => [c.ref, `${USER_PREFIX}${c.targetUserId}`]));
  return JSON.parse(
    JSON.stringify(definition, (_k, v: unknown) =>
      typeof v === 'string' ? (byRef.get(v) ?? v) : v,
    ),
  ) as BuilderDefinition;
};

/** Marks a pick that isn't a destination yet — a person we'd DM on save. */
export const USER_PREFIX = 'user:';

/** `workflowId === 'new'` = create mode. */
export const useBuilderEditorLogic = (workflowId: string | undefined) => {
  const { data: me } = useMe();
  const tenantId = me?.tenant.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isNew = workflowId === 'new';

  const overview = useQuery({
    queryKey: queryKeys.workflows(tenantId ?? ''),
    queryFn: () => api.fetchOverview(),
    enabled: Boolean(tenantId),
  });
  const workflow = useMemo(() => {
    if (!overview.data || isNew) return null;
    return flattenOverview(overview.data).find((w) => w.id === workflowId) ?? null;
  }, [overview.data, workflowId, isNew]);

  // ── Draft state ──
  // A template is the INITIAL state of a new draft, not an effect that
  // overwrites one: `?from=` is read at mount, so editing then reloading
  // still starts from the template, and nothing races the user's typing.
  const [params] = useSearchParams();
  const template = isNew ? templateById(params.get('from')) : undefined;
  const [draft, setDraft] = useState<BuilderDefinition | null>(template?.definition ?? null);
  const [name, setName] = useState<string | null>(template?.name ?? null);
  // Draft-overridable like `name`: null = "whatever the saved workflow has".
  const [trigger, setTrigger] = useState<string | null>(template ? template.trigger : null);
  /** Null until this workflow is scheduled; the picker seeds it when chosen. */
  const [schedule, setSchedule] = useState<BuilderSchedule | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Which step's config drawer is open, as a path key ("1" / "1.cases.0.steps.0"). */
  const [expandedPath, setExpandedPath] = useState<string | null>(null);
  const definition = draft ?? workflow?.definition ?? NEW_DEFINITION;
  const displayName = name ?? workflow?.name ?? '';

  const [savedAt, setSavedAt] = useState<number | null>(null);

  /**
   * Lay the previewed steps down one at a time. Nothing is being computed —
   * the definition already arrived whole — but a workflow that appears all at
   * once is a wall to re-read, where one arriving in execution order walks the
   * eye down it. Bounded to REVEAL_TOTAL_MS in total, so a long workflow does
   * not become a wait, and skipped entirely for reduced-motion.
   */
  const revealTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [revealing, setRevealing] = useState(false);
  /** What the saved workflow looked like before the helper touched it. */
  const previewBaseline = useRef<{
    definition: BuilderDefinition;
    name: string | null;
    trigger: string | null;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const revealSteps = (next: BuilderDefinition) => {
    for (const t of revealTimers.current) clearTimeout(t);
    revealTimers.current = [];
    const count = next.steps.length;
    const instant =
      count <= 1 ||
      (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    if (instant) {
      setRevealing(false);
      setDraft(next);
      return;
    }
    const gap = Math.min(REVEAL_STEP_MS, REVEAL_TOTAL_MS / count);
    setRevealing(true);
    for (let i = 1; i <= count; i += 1) {
      revealTimers.current.push(
        setTimeout(() => {
          setDraft({ ...next, steps: next.steps.slice(0, i) });
          // The waiting row goes when the last step lands, never before.
          if (i === count) setRevealing(false);
        }, gap * i),
      );
    }
  };
  /**
   * Autosave, scheduled from the handlers that change things — not an effect
   * watching state. A draft that isn't enabled can't do anything, so there is
   * nothing for a person to decide by pressing Save; a new workflow still
   * needs one explicit Create, because until then there is no row to write to.
   */
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAutosave = () => {
    if (isNew) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => saveRef.current(), 800);
  };

  const edit = (next: BuilderDefinition) => {
    setDraft(next);
    setDirty(true);
    setTestRunId(null); // a changed draft invalidates the last test
    scheduleAutosave();
  };

  // ── Test rail ──
  const effectiveTrigger = trigger ?? workflow?.trigger ?? '';
  const effectiveSchedule = schedule ?? workflow?.schedule ?? null;
  const recentEvents = useQuery({
    queryKey: ['builder-recent-events', effectiveTrigger],
    queryFn: () => api.fetchRecentEvents(effectiveTrigger),
    enabled: Boolean(effectiveTrigger),
  });
  const [testEventId, setTestEventId] = useState('');
  const events = recentEvents.data?.events ?? [];
  const selectedEventId = testEventId || (events[0]?.id ?? '');

  /**
   * A test is a REAL run on the orchestration deployable: start it, then poll
   * until terminal. `user:<id>` destination picks materialize first — the
   * runner previews against real channel rows, same as a save would create.
   */
  const [testRunId, setTestRunId] = useState<string | null>(null);
  // The clock is a scheduled workflow's trigger — there may never have been a
  // first event, so its test is "Run now": the server synthesizes the
  // occurrence item the ticker would enqueue.
  const isScheduled = Boolean(effectiveSchedule);
  const test = useMutation({
    mutationFn: async () => {
      const materialized = await serializeRecipients(definition);
      return api.createTestRun({
        workflowId: workflowId ?? '',
        definition: materialized,
        ...(isScheduled ? {} : { eventId: selectedEventId }),
      });
    },
    onSuccess: (r) => {
      setTestRunId(r.runId);
    },
  });
  const testRunQuery = useQuery({
    queryKey: ['builder-test-run', testRunId],
    queryFn: () => api.fetchTestRun(testRunId ?? ''),
    enabled: testRunId !== null,
    // A step lands every couple of seconds mid-run; stop once terminal.
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'running' ? false : 1_500),
  });

  // ── Destinations ──
  const channelsQuery = useQuery({
    queryKey: queryKeys.notificationChannels(tenantId ?? ''),
    queryFn: () => api.fetchChannels(),
    enabled: Boolean(tenantId),
  });
  const usersQuery = useQuery({
    queryKey: queryKeys.users(tenantId ?? ''),
    queryFn: () => api.fetchUsers(),
    enabled: Boolean(tenantId),
  });
  const channels = channelsQuery.data?.channels ?? [];
  const users = usersQuery.data?.users ?? [];

  // Preserve references in existing workflows without offering shared channels for new sends.
  const selectedChannelIds = new Set<string>();
  const collectLegacyRecipients = (steps: Step[]): void => {
    for (const step of steps) {
      if (step.type === 'notify') step.channelIds.forEach((id) => selectedChannelIds.add(id));
      if (step.type === 'branch') {
        step.cases.forEach((branch) => collectLegacyRecipients(branch.steps));
        collectLegacyRecipients(step.otherwise);
      }
      if (step.type === 'approval') {
        collectLegacyRecipients(step.onApprove);
        collectLegacyRecipients(step.onDecline);
      }
    }
  };
  collectLegacyRecipients(definition.steps);
  const destinationOptions = [
    ...channels
      .filter((channel) => selectedChannelIds.has(channel.id))
      .map((c) => ({ value: c.id, label: c.label })),
    ...users.map((u) => ({
      value: `${USER_PREFIX}${u.id}`,
      label: `${u.name ?? u.email} (person)`,
    })),
  ];

  const peopleOptions = users.map((u) => ({ value: u.id, label: u.name ?? u.email }));
  /** Store users directly; retain existing legacy destination IDs. */
  const serializeRecipients = async (def: BuilderDefinition): Promise<BuilderDefinition> => {
    const walk = async (steps: Step[]): Promise<Step[]> =>
      Promise.all(
        steps.map(async (step): Promise<Step> => {
          if (step.type === 'notify') {
            const recipients = [...(step.recipients ?? [])];
            const channelIds = step.channelIds.filter((id) => {
              const kind = id.startsWith('user:') ? 'user' : null;
              if (!kind) return true;
              recipients.push({ kind, id: id.slice(kind.length + 1) });
              return false;
            });
            return { ...step, channelIds, recipients };
          }
          if (step.type === 'approval') {
            return {
              ...step,
              onApprove: await walk(step.onApprove),
              onDecline: await walk(step.onDecline),
            };
          }
          if (step.type === 'branch') {
            return {
              ...step,
              cases: await Promise.all(
                step.cases.map(async (c) => ({ ...c, steps: await walk(c.steps) })),
              ),
              otherwise: await walk(step.otherwise),
            };
          }
          return step;
        }),
      );
    return { ...def, steps: await walk(def.steps) };
  };

  // ── Persistence ──
  const saveRef = useRef<() => void>(() => undefined);
  const save = useMutation({
    mutationFn: async () => {
      // Convert picker values into direct recipient references before saving.
      const ready = await serializeRecipients(definition);
      setDraft(ready);
      if (isNew) {
        const created = await api.createComposed({
          name: displayName || 'Untitled workflow',
          trigger: effectiveTrigger,
          definition: ready,
          // Same guard as the edit path: a draft schedule survives switching
          // the trigger back to an event, and must not ride along.
          ...(effectiveSchedule && isScheduleTrigger(effectiveTrigger)
            ? { schedule: effectiveSchedule }
            : {}),
        });
        // The API gives a scheduled workflow its permanent trigger key.
        // This component stays mounted when /new becomes /:id.
        setTrigger(
          isScheduleTrigger(effectiveTrigger) ? `schedule:${created.id}` : effectiveTrigger,
        );
        return created.id;
      }
      await api.updateComposed(workflowId ?? '', {
        ...(name !== null ? { name } : {}),
        // '' is the picker's transient mode-switch state, not a choice — keep
        // the saved trigger rather than PATCHing an invalid empty string.
        ...(trigger !== null && trigger !== '' ? { trigger } : {}),
        // Only on a scheduled workflow: the server refuses one anywhere else.
        ...(schedule && isScheduleTrigger(effectiveTrigger) ? { schedule } : {}),
        definition: ready,
      });
      return workflowId ?? '';
    },
    onSuccess: (id) => {
      setDirty(false);
      setSavedAt(Date.now());
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notificationChannels(tenantId ?? ''),
      });
      if (isNew) navigate(`/settings/workflows/${id}`, { replace: true });
    },
  });

  const enable = useMutation({
    mutationFn: (enabled: boolean) => api.setWorkflowEnabled(workflowId ?? '', enabled),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') }),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteComposed(workflowId ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') });
      navigate('/settings/workflows');
    },
  });

  saveRef.current = () => save.mutate();

  /** The rail's "Lately" list — the same runs the history page shows, capped.
   * Skipped for a workflow that doesn't exist yet. */
  const recentRuns = useQuery({
    queryKey: ['builder-runs', tenantId, workflowId],
    queryFn: () => api.fetchRuns(workflowId ?? ''),
    enabled: Boolean(tenantId && workflowId && !isNew),
  });

  const saveSystemConfig = useMutation({
    mutationFn: (config: unknown) => api.updateSystemConfig(workflowId ?? '', config),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows(tenantId ?? '') }),
  });

  // Enable is not gated in the client. It used to require `testResults !== null`,
  // which only proved a request came back — a test whose every step was skipped
  // unlocked it just the same. The server checks the definition is actually
  // runnable and answers with a plain list of what's missing, which is the real
  // gate; the button stays live so the user can see that list.

  // The trigger's registry-declared fields — the variable picker's stand-in
  // until a real event provides samples (workflow-first authoring).
  const triggerFields = useMemo(
    () => overview.data?.triggers.find((t) => t.type === effectiveTrigger)?.fields ?? [],
    [overview.data, effectiveTrigger],
  );

  return {
    isNew,
    isLoading: overview.isLoading,
    workflow,
    approverCandidates: overview.data?.approverCandidates ?? [],
    stepTypes: knownStepTypes(overview.data?.stepTypes ?? []),
    personas: overview.data?.personas ?? [],
    // The catalog as the overview serves it: the tenant's approved types
    // this author may build on, plus the platform's own.
    platformTriggers: overview.data?.triggers ?? [],
    triggerFields,
    definition,
    displayName,
    setDisplayName: (v: string) => {
      setName(v);
      setDirty(true);
      scheduleAutosave();
    },
    trigger: effectiveTrigger,
    schedule: effectiveSchedule,
    setSchedule: (v: BuilderSchedule) => {
      setSchedule(v);
      setDirty(true);
      scheduleAutosave();
    },
    setTrigger: (v: string) => {
      setTrigger(v);
      if (!isNew) {
        setDirty(true);
        // '' is the picker's transient mode-switch state — saving it would
        // PATCH an invalid trigger, so wait for a real choice.
        if (v !== '') scheduleAutosave();
      }
    },
    dirty,
    // tree edits
    /**
     * Live preview during an interview: state only. It deliberately does NOT
     * go through `edit`, so nothing is marked dirty and autosave never fires —
     * a half-built workflow must not be able to reach the server. Offered for
     * a NEW workflow only: overwriting someone's existing draft mid-chat, with
     * something they haven't agreed to, is not a preview.
     */
    previewDraft: (draft: {
      name?: string;
      trigger?: string;
      definition?: unknown;
      pendingChannels?: { ref: string; targetUserId: string }[];
    }) => {
      // On a SAVED workflow, remember what was there before the helper started
      // changing things — a preview you cannot back out of is just an edit.
      // Captured once, on the first preview of a conversation.
      if (!isNew && previewBaseline.current === null) {
        previewBaseline.current = { definition, name, trigger };
        setPreviewing(true);
      }
      if (draft.name) setName(draft.name);
      if (draft.trigger) setTrigger(draft.trigger);
      const def = draft.definition;
      if (!def || typeof def !== 'object' || !Array.isArray((def as BuilderDefinition).steps))
        return;
      // Same ref resolution as apply: a previewed step must hold the marker
      // the editor can actually save, not a placeholder only the chat knows.
      const next = resolveChannelRefs(def as BuilderDefinition, draft.pendingChannels);
      revealSteps(next);
    },
    /**
     * Take a helper proposal into the draft on screen: name, trigger and steps
     * in one edit. Its pending DM channels are rewritten to the editor's own
     * `user:<id>` markers, so the SAME save-time path that materialises a
     * hand-picked person creates them — one write path, and the helper never
     * needs a create endpoint of its own.
     */
    applyProposal: (proposal: {
      name?: string;
      trigger?: string;
      definition: BuilderDefinition;
      pendingChannels?: { ref: string; targetUserId: string }[];
    }) => {
      const resolved = resolveChannelRefs(proposal.definition, proposal.pendingChannels);
      if (proposal.name) setName(proposal.name);
      if (proposal.trigger) setTrigger(proposal.trigger);
      // Applied: this IS the draft now, so there is nothing to back out of.
      previewBaseline.current = null;
      setPreviewing(false);
      edit(resolved);
      // Applying IS saving. On an existing workflow `edit` already scheduled
      // the write; a new one has no row yet, so create it — on the next tick,
      // once the state above has rendered and `saveRef` closes over it.
      if (isNew) setTimeout(() => saveRef.current(), 0);
    },
    setCreateCase: (createCase: boolean) => edit({ ...definition, createCase }),
    expandedPath,
    onToggle: (k: string) => {
      setExpandedPath((p) => (p === k ? null : k));
    },
    /** Every edit to the step tree, handed to the step list as one bundle. */
    stepEdit: {
      onReplace: (path: StepPath, step: BuilderStep) => edit(replaceStep(definition, path, step)),
      onInsert: (containerPath: StepPath, type: BuilderStep['type']) => {
        const fresh = defaultStep(type);
        // A bot step starts on the event's email field when there's exactly one.
        const step =
          fresh.type === 'bot'
            ? {
                ...fresh,
                recipients: {
                  kind: 'from_event' as const,
                  field: obviousPersonField(
                    triggerFields.map((f) => ({
                      path: `payload.${f.name}`,
                      label: f.label ?? f.name,
                    })),
                  ),
                },
              }
            : fresh;
        const next = insertStep(definition, containerPath, step);
        edit(next);
        // A step you just added is a step you're about to fill in — open it.
        setExpandedPath([...containerPath, listLengthAt(next, containerPath) - 1].join('.'));
      },
      onRemove: (path: StepPath) => edit(removeStep(definition, path)),
      onMove: (path: StepPath, dir: -1 | 1) => edit(moveStep(definition, path, dir)),
      onAddCase: (branchPath: StepPath) => edit(addCase(definition, branchPath)),
      onRemoveCase: (branchPath: StepPath, i: number) =>
        edit(removeCase(definition, branchPath, i)),
      onUpdateCase: (branchPath: StepPath, i: number, patch: Parameters<typeof updateCase>[3]) =>
        edit(updateCase(definition, branchPath, i, patch)),
    } satisfies StepEditApi,
    destinationOptions,
    peopleOptions,
    // test rail
    recentEvents: events,
    selectedEventId,
    setTestEventId,
    /** Where the value list comes from — the fields registered on the trigger. */
    eventSource: triggerFields.length > 0 ? `fields registered on ${effectiveTrigger}` : null,
    testRun: testRunId !== null ? (testRunQuery.data ?? null) : null,
    testRunning: test.isPending || (testRunId !== null && testRunQuery.data?.status === 'running'),
    testError: test.error,
    runTest: () => test.mutate(),
    canTest: (isScheduled || Boolean(selectedEventId)) && !isNew,
    isScheduled,
    // persistence
    save: () => save.mutate(),
    saving: save.isPending,
    savedAt,
    /** Mid-reveal: the page shows a waiting row where the next step will go. */
    revealing,
    /** Showing the helper's unapplied changes over a saved workflow. */
    previewing,
    discardPreview: () => {
      const base = previewBaseline.current;
      if (!base) return;
      for (const t of revealTimers.current) clearTimeout(t);
      revealTimers.current = [];
      setRevealing(false);
      setDraft(base.definition);
      setName(base.name);
      setTrigger(base.trigger);
      previewBaseline.current = null;
      setPreviewing(false);
    },
    recentRuns: (recentRuns.data ?? []).slice(0, 3),
    saveError: save.error,
    enableError: enable.error,
    enabled: workflow?.enabled ?? false,
    setEnabled: (v: boolean) => enable.mutate(v),
    deleteWorkflow: () => remove.mutate(),
    saveSystemConfig: (config: unknown) => saveSystemConfig.mutate(config),
    savingSystemConfig: saveSystemConfig.isPending,
  };
};

export const useBuilderRunsLogic = (workflowId: string | undefined) => {
  const { data: me } = useMe();
  const tenantId = me?.tenant.id;

  const overview = useQuery({
    queryKey: queryKeys.workflows(tenantId ?? ''),
    queryFn: () => api.fetchOverview(),
    enabled: Boolean(tenantId),
  });
  const workflow = useMemo(() => {
    if (!overview.data) return null;
    return flattenOverview(overview.data).find((w) => w.id === workflowId) ?? null;
  }, [overview.data, workflowId]);

  const runs = useQuery({
    queryKey: ['builder-runs', tenantId, workflowId],
    queryFn: () => api.fetchRuns(workflowId ?? ''),
    enabled: Boolean(tenantId && workflowId),
  });

  const [openRunId, setOpenRunId] = useState<string | null>(null);
  return {
    workflow,
    runs: runs.data ?? [],
    isLoading: runs.isLoading,
    openRunId,
    toggleRun: (id: string) => {
      setOpenRunId((r) => (r === id ? null : id));
    },
  };
};
